/**
 * Voiding a subscription that should never have existed.
 *
 * A cancellation and a void are different events and must not be confused. A
 * cancellation is a real thing that happened: the member joined, attended, then
 * stopped, and the refund is a negative payment so the money movement stays on
 * the record. A VOID is the claim that the sale never took place at all — a
 * wrong class, a duplicate, a name typed into the wrong member. There is no
 * money to move, because no money ever moved.
 *
 * So a void must not write a compensating negative payment. That would leave
 * a matched pair of rows in the ledger describing cash that never crossed the
 * desk, and a till that is reconciled against them will not balance.
 *
 * Instead the subscription is soft-deleted, and every money figure ignores the
 * payments that hang off it. That is what this module is for: `deleted_at` on
 * a subscription already hides it from member lists and status counts, but
 * revenue is summed straight off the `payments` table, which knows nothing
 * about the subscription it belongs to. Without the filter below, voiding a
 * subscription hides the subscription and leaves its money in the totals.
 *
 * Doing it this way is what lets `payments` keep its guarantee of having no
 * UPDATE and no DELETE policy for anybody. Nothing here edits a payment row.
 * The rows survive, so a void is reversible: clearing `deleted_at` on the
 * subscription brings it and its money back exactly as they were.
 */

/** The parts of a payment this module needs. Keeps it usable from tests. */
export interface PaymentLike {
  subscription_id: string;
}

/** The parts of a subscription this module needs. */
export interface SubscriptionLike {
  id: string;
}

/**
 * Drop the payments belonging to subscriptions that are not in `live`.
 *
 * Pass the ALREADY-FILTERED live subscriptions — this deliberately keys off
 * presence rather than re-reading `deleted_at`, so there is one definition of
 * "live" in the codebase (the `live()` helper) instead of two that can drift.
 *
 * A payment whose subscription is missing is dropped. During a sync the
 * payments table can briefly be ahead of the subscriptions table, which makes
 * a figure dip for one render before the rest arrives. That is the right way
 * round: a total that is briefly low corrects itself on the next pull, whereas
 * counting an orphan means reporting revenue nobody can point at a sale for.
 */
export function dropVoidedPayments<P extends PaymentLike>(
  payments: readonly P[],
  live: readonly SubscriptionLike[]
): P[] {
  const liveIds = new Set(live.map((s) => s.id));
  return payments.filter((p) => liveIds.has(p.subscription_id));
}
